"use client";

import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, MapPin, Search, ChartBar as BarChart3, ShieldCheck, ArrowRight, Target, Compass, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const telfordKeywords = [{
  id: "kw-1",
  keyword: "SEO Consultant Telford",
  volume: "High Intent",
  difficulty: "Moderate",
  opportunity: "Direct lead capture for high-value services"
}, {
  id: "kw-2",
  keyword: "B2B Marketing Telford",
  volume: "Growing",
  difficulty: "Low-Medium",
  opportunity: "Shropshire industrial park sector demand"
}, {
  id: "kw-3",
  keyword: "Local SEO Services Telford",
  volume: "High Conversion",
  difficulty: "Moderate",
  opportunity: "Google Map Pack dominance gap"
}, {
  id: "kw-4",
  keyword: "Technical SEO Shropshire",
  volume: "Niche High Value",
  difficulty: "Low",
  opportunity: "E-commerce and multi-site optimization"
}];
const competitorInsights = [{
  id: "comp-1",
  title: "Google Map Pack Gaps",
  desc: "Over 60% of Telford service businesses lack fully optimized Google Business Profiles, structured citations, and localized review management."
}, {
  id: "comp-2",
  title: "Generic Agency Content",
  desc: "Competitors rely on template-driven local pages with minimal Shropshire context, creating an easy path for genuine expert content to rank."
}, {
  id: "comp-3",
  title: "Weak Technical Foundations",
  desc: "Slow page load speeds and broken localized schema markups are widespread among local businesses across Stafford Park and Halesfield."
}];
const neighboringLocations = [{
  id: "loc-1",
  name: "Wolverhampton",
  path: "/LocalSeoConsultantWolverhamptonPage",
  distance: "18 miles",
  focus: "Industrial & Manufacturing SEO"
}, {
  id: "loc-2",
  name: "Stoke-on-Trent",
  path: "/LocalSeoConsultantStokeOnTrentPage",
  distance: "32 miles",
  focus: "E-commerce & Local Services"
}, {
  id: "loc-3",
  name: "Derby",
  path: "/LocalSeoConsultantDerbyPage",
  distance: "55 miles",
  focus: "Tech & B2B Lead Generation"
}, {
  id: "loc-4",
  name: "Milton Keynes",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  distance: "85 miles",
  focus: "Corporate & Enterprise SEO"
}, {
  id: "loc-5",
  name: "Northampton",
  path: "/LocalSeoConsultantNorthamptonPage",
  distance: "80 miles",
  focus: "Logistics & B2B Marketing"
}, {
  id: "loc-6",
  name: "Warrington",
  path: "/LocalSeoConsultantWarringtonPage",
  distance: "60 miles",
  focus: "Distribution & Multi-Location SEO"
}];
export default function Telford() {
  return <section id="telfordlocalseoinsights" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 gap-1 px-3 py-1 text-xs font-medium">
            <Compass className="size-3.5 text-primary" />
            Shropshire & Telford Search Market Analysis
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold font-serif tracking-tight text-foreground mb-6">
            Telford Local SEO Insights & Search Opportunities
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            As an independent freelance SEO consultant working directly with businesses across Telford and Shropshire, I analyze local search demand, competitor blind spots, and technical SEO gaps to deliver sustainable search engine growth. You work directly with me—no account managers or generic templates.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7 flex flex-col justify-between">
            <Card className="h-full border border-border bg-card shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary">
                      <TrendingUp className="size-5" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-semibold font-serif">
                        Telford High-Value Keyword Landscape
                      </CardTitle>
                      <CardDescription>
                        Key organic search queries mapped for commercial opportunity
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant="outline" className="hidden sm:inline-flex">
                    Live Opportunity Data
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {telfordKeywords.map((item, i) => <div data-index={i} key={item.id} className="p-4 rounded-lg bg-muted/50 border border-border/60 hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <Search className="size-4 text-primary shrink-0" />
                        <span className="font-semibold text-foreground text-sm sm:text-base">
                          {item.keyword}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">
                          {item.volume}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-secondary text-secondary-foreground font-medium">
                          {item.difficulty}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground pl-6">
                      {item.opportunity}
                    </p>
                  </div>)}
              </CardContent>
            </Card>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-5 flex flex-col justify-between">
            <Card className="h-full border border-border bg-card shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-accent/20 text-accent-foreground">
                    <BarChart3 className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-semibold font-serif">
                      Telford Competitor Analysis
                    </CardTitle>
                    <CardDescription>
                      Where local competitors are losing search market share
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {competitorInsights.map((comp, i) => <div data-index={i} key={comp.id} className="p-4 rounded-lg bg-background border border-border/80">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="size-4 text-accent shrink-0" />
                      <h3 className="font-medium text-foreground text-sm sm:text-base">
                        {comp.title}
                      </h3>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {comp.desc}
                    </p>
                  </div>)}

                <div className="pt-2">
                  <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 text-xs sm:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground block mb-1">
                      Direct Consultant Strategic Advantage:
                    </span>
                    I perform bespoke technical audits and build localized entity relationships that allow Telford businesses to outperform generic agency campaigns.
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-16 p-6 sm:p-8 rounded-2xl bg-muted/40 border border-border">
          <div className="max-w-3xl mb-8">
            <h3 className="text-2xl font-semibold font-serif text-foreground mb-3">
              Strategic Opportunities Summary for Telford Businesses
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              My bespoke SEO strategy focuses on three core pillars designed to capture qualified local commercial intent across Shropshire and surrounding commercial hubs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div data-index={0} className="p-5 rounded-xl bg-card border border-border shadow-xs">
              <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit mb-4">
                <MapPin className="size-5" />
              </div>
              <h4 className="font-semibold text-foreground text-base mb-2">
                Map Pack Dominance
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Structured citation optimization, localized review velocity strategy, and Google Business Profile geotagging tailored for Telford town center and commercial parks.
              </p>
            </div>

            <div data-index={1} className="p-5 rounded-xl bg-card border border-border shadow-xs">
              <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit mb-4">
                <Sparkles className="size-5" />
              </div>
              <h4 className="font-semibold text-foreground text-base mb-2">
                AI & Local Search Synergy
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Optimizing your business entities for Google AI Overviews and ChatGPT local discovery queries so your brand is recommended first.
              </p>
            </div>

            <div data-index={2} className="p-5 rounded-xl bg-card border border-border shadow-xs">
              <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit mb-4">
                <ShieldCheck className="size-5" />
              </div>
              <h4 className="font-semibold text-foreground text-base mb-2">
                Technical SEO Excellence
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Fixing crawl efficiency, Core Web Vitals, and JSON-LD schema so search engine crawlers accurately index your localized service offerings.
              </p>
            </div>
          </div>
        </motion.div>

        {}
        <div className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <h3 className="text-2xl font-semibold font-serif text-foreground mb-2">
                Cross-Regional Local SEO Coverage
              </h3>
              <p className="text-sm text-muted-foreground">
                Delivering tailored organic search consultancy across neighboring commercial hubs.
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="w-fit">
              <Link to="/ServicesPage">View All Services</Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringLocations.map((loc, i) => <div data-index={i} key={loc.id}>
                <Link to={loc.path} className="group block p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-[border-color,box-shadow] duration-200 shadow-xs">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-foreground group-hover:text-primary transition-colors text-base flex items-center gap-2">
                      <MapPin className="size-4 text-primary shrink-0" />
                      {loc.name}
                    </span>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                      {loc.distance}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    {loc.focus}
                  </p>
                  <div className="flex items-center text-xs font-medium text-primary gap-1 group-hover:translate-x-1 transition-transform">
                    <span>Explore Local Strategy</span>
                    <ArrowRight className="size-3" />
                  </div>
                </Link>
              </div>)}
          </div>
        </div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center p-8 sm:p-12 rounded-2xl bg-primary text-primary-foreground relative overflow-hidden">
          <div className="max-w-2xl mx-auto relative z-10">
            <h3 className="text-2xl sm:text-3xl font-semibold font-serif mb-4">
              Ready to Outrank Competitors in Telford?
            </h3>
            <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed mb-8">
              Book a direct consultation with me to discuss your local SEO goals, review your search opportunities, and create a roadmap tailored specifically to your business.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto font-medium">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>

            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-primary-foreground/80">
              <CheckCircle2 className="size-4 text-accent" />
              <span>Direct Consultant Access • Tailored Local Strategy • Transparent Reporting</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}